import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportWeatherSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { DirectionEnum, NoonReportSectionEnum, NoonReportTypeEnum, WindForceEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import {
    convertWaveHeightToSeaState,
    createSectionData,
    enumToArray,
    getDirectionPresentationString,
    getNumberValue,
    getWindForceEnum,
    getWindForcePresentationString,
    hasInvalidFields,
    hasMissingValues,
} from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { WeatherSectionSelectStyle } from './WeatherSection.style';
import { roundTo2Decimals } from '../../../utils/numberHelper';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { isNullOrUndefined } from 'src/utils/objectUtils';

type VM = {
    badWeatherHours?: number;
    airTemperature?: number;
    seaTemperature?: number;
    currentForce?: number;
    waveHeight?: number;
    seaState?: string;
    seaDirection?: string;
    windForce?: string;
    windDirection?: string;
    currentDirection?: string;
    sectionData?: NoonReportSectionData;
    calmWeatherDefinition?: number;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const WeatherSectionComponent: React.FC = () => {
    const [weatherVM, setWeatherVM] = useState<VM>({});
    const [windForces, setWindForces] = useState<string[]>([]);
    const [directions, setDirections] = useState<string[]>([]);
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const { selectedAolDataItem, aolSyncEvent } = useAolStore();
    const { vessel } = useVesselStore();
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!weatherVM.isInitialized) {
            initVM();
        }

        setWindForces([...enumToArray(WindForceEnum, getWindForcePresentationString)]);
        setDirections([...enumToArray(DirectionEnum, getDirectionPresentationString)]);
    }, [selectedAolDataItem]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.Weather &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(weatherVM?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;
        const waveHeight = getNumberValue(noonReport?.weatherSection?.waveHeight);
        const currentDirection = getNumberValue(noonReport?.weatherSection?.currentDirection);
        const seaDirection = getNumberValue(noonReport?.weatherSection?.seaDirection);
        const windForce = getNumberValue(noonReport?.weatherSection?.windForce);
        const windDirection = getNumberValue(noonReport?.weatherSection?.windDirection);

        const vm: VM = {
            airTemperature: getNumberValue(noonReport?.weatherSection?.airTemperature, 1),
            seaTemperature: getNumberValue(noonReport?.weatherSection?.seaTemperature, 1),
            waveHeight: getNumberValue(noonReport?.weatherSection?.waveHeight, 1),
            currentForce: getNumberValue(noonReport?.weatherSection?.currentForce, 1, true),
            seaState: !isNullOrUndefined(waveHeight) ? convertWaveHeightToSeaState(waveHeight) : undefined,
            currentDirection: !isNullOrUndefined(currentDirection) ? getDirectionPresentationString(currentDirection) : undefined,
            badWeatherHours: getNumberValue(noonReport?.weatherSection?.badWeatherHours, undefined, true),
            seaDirection: !isNullOrUndefined(seaDirection) ? getDirectionPresentationString(seaDirection) : undefined,
            windForce: !isNullOrUndefined(windForce) ? getWindForcePresentationString(windForce) : undefined,
            windDirection: !isNullOrUndefined(windDirection) ? getDirectionPresentationString(windDirection) : undefined,
            calmWeatherDefinition: getNumberValue(vessel?.maxBeaufortInCalmWeather),
            sectionData: noonReport?.weatherSection?.sectionData ?? createSectionData(NoonReportSectionEnum.Weather),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };

        setWeatherVM({ ...vm });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        const type = changeObject.event.target.type;

        if (type === 'select-one') {
            weatherVM[name] = value ? value : undefined;
        } else {
            weatherVM[name] = value ? +value : undefined;
        }

        if (name === 'waveHeight') {
            if (value !== undefined && value !== null) {
                weatherVM.seaState = convertWaveHeightToSeaState(+value);
            } else {
                weatherVM.seaState = undefined;
            }
        }

        if (hasEmptyValues) setHasEmptyValues(false);
        hasInvalidFields(weatherVM, name, changeObject.isValid);
        setWeatherVM({ ...weatherVM });
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        hasInvalidFields(weatherVM, changeObject.name, changeObject.isValid);
        setWeatherVM({ ...weatherVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList = ['ecaZoneHours'];
        if (selectedAolDataItem?.reportType !== NoonReportTypeEnum.Sea) {
            ignoreList.push(
                'waveHeight',
                'seaState',
                'windForce',
                'currentDirection',
                'seaDirection',
                'windDirection',
                'currentForce',
                'badWeatherHours'
            );
        }

        if (!selectedAolDataItem?.isReportAtNoon) {
            ignoreList.push('airTemperature');
            ignoreList.push('seaTemperature');
        }

        if (!weatherVM?.calmWeatherDefinition) {
            ignoreList.push('calmWeatherDefinition');
        }

        if (!isInBadWeather()) {
            ignoreList.push('badWeatherHours');
        }

        if (hasMissingValues(weatherVM, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportWeatherSectionFactory().createBM(weatherVM);
            if (!data.sectionData || data.sectionData.section === 0)
                data.sectionData = {
                    noonReportId: 0,
                    section: NoonReportSectionEnum.Weather,
                    isApproved: false,
                    isCompleted: false,
                    role: 1,
                };
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!weatherVM.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    const isInBadWeather = () => {
        if (isNullOrUndefined(vessel?.maxBeaufortInCalmWeather) || isNullOrUndefined(weatherVM)) {
            return false;
        }

        const windForce = getWindForceEnum(weatherVM?.windForce);
        return vessel?.maxBeaufortInCalmWeather < windForce;
    };

    return (
        <>
            {selectedAolDataItem && selectedAolDataItem.isReportAtNoon && (
                <>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'number'}
                        name={'airTemperature'}
                        description={'Air temperature'}
                        min={-100}
                        max={100}
                        value={weatherVM.airTemperature}
                        onChange={onInputChange}
                        unit={'℃'}
                        leaveBlank={true}
                        allowNegativeValues={true}
                    />
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'number'}
                        name={'seaTemperature'}
                        description={'Sea temperature'}
                        min={-10}
                        max={50}
                        value={weatherVM.seaTemperature}
                        onChange={onInputChange}
                        unit={'℃'}
                        leaveBlank={true}
                        allowNegativeValues={true}
                    />
                </>
            )}
            {selectedAolDataItem && selectedAolDataItem.reportType === NoonReportTypeEnum.Sea && (
                <>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'number'}
                        name={'waveHeight'}
                        description={'Wave height'}
                        min={0}
                        max={20}
                        value={weatherVM.waveHeight}
                        onChange={onInputChange}
                        unit={'m'}
                        leaveBlank={true}
                    />
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'text'}
                        description={'Sea state'}
                        value={weatherVM.seaState}
                        onChange={onInputChange}
                        unit={''}
                    />
                    <WeatherSectionSelectStyle>
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'select'}
                            name={'windForce'}
                            description={'Wind force'}
                            value={weatherVM.windForce}
                            onChange={onInputChange}
                            options={windForces}
                            selectDefaultOption={'Select location'}
                        />
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'select'}
                            name={'currentDirection'}
                            description={'Current direction'}
                            value={weatherVM.currentDirection}
                            onChange={onInputChange}
                            options={directions}
                            selectDefaultOption={'Select location'}
                        />
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'select'}
                            name={'seaDirection'}
                            description={'Sea direction'}
                            value={weatherVM.seaDirection}
                            onChange={onInputChange}
                            options={directions}
                            selectDefaultOption={'Select location'}
                        />
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'select'}
                            name={'windDirection'}
                            description={'Wind direction'}
                            value={weatherVM.windDirection}
                            onChange={onInputChange}
                            options={directions}
                            selectDefaultOption={'Select location'}
                        />
                    </WeatherSectionSelectStyle>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'number'}
                        name={'currentForce'}
                        description={'Current force'}
                        min={0}
                        max={5}
                        value={weatherVM.currentForce}
                        onChange={onInputChange}
                        unit={'knot'}
                    />
                    {isInBadWeather() && (
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'time'}
                            name={'badWeatherHours'}
                            description={'Bad weather hours'}
                            value={weatherVM.badWeatherHours}
                            max={roundTo2Decimals(selectedAolDataItem.reportHours)}
                            dataObject={weatherVM}
                            onTimeInputChange={onTimeInputChange}
                        />
                    )}
                    {weatherVM.calmWeatherDefinition !== undefined && weatherVM.calmWeatherDefinition > 0 && (
                        <OdfjellInputComponent
                            type={'number'}
                            isReadOnly={true}
                            description={'Calm weather definition'}
                            value={weatherVM.calmWeatherDefinition}
                            onChange={onInputChange}
                            unit={''}
                        />
                    )}
                    {weatherVM.calmWeatherDefinition !== undefined && weatherVM.calmWeatherDefinition <= 0 && <span>No calm weather definition</span>}
                </>
            )}
            {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
        </>
    );
};

export default WeatherSectionComponent;
